import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { ReportType } from "../types/reportType";
/* eslint-disable import/export */
export abstract class RequestSurveyReportRPC implements
  LeoRPC<RequestSurveyReportRPC.Request, RequestSurveyReportRPC.Response, RequestSurveyReportRPC.Errors.Errors>
{
  abstract execute(
    request: RequestSurveyReportRPC.Request
  ): Promise<LeoRPCResult<RequestSurveyReportRPC.Response, RequestSurveyReportRPC.Errors.Errors>>;
}

export namespace RequestSurveyReportRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace LinkType {
      export enum LinkType {
        LIVE = "LIVE",
        TEST = "TEST",
      }
    
      export interface LinkTypeDTO {
        case: string;
      }
    
      export const fromDTO = (dto: LinkTypeDTO): LinkType => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestLinkTypeError(
            `Invalid DTO ${dto} provided for LinkType.LinkType`
          );
        }
        switch (dto.case) {
          case "LIVE":
            return LinkType.LIVE;
          case "TEST":
            return LinkType.TEST;
          default:
            throw new InvalidRequestLinkTypeError(
              `Case ${dto.case} is not valid case of LinkType.LinkType`
            );
        }
      };
    
      export const toDTO = (linkType: LinkType): LinkTypeDTO => {
        const enumDTO = {
          case: LinkType[linkType],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    surveyId: string;
    projectId: string;
    reportType: ReportType.ReportTypeDTO;
    linkType: RequestEnums.LinkType.LinkTypeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isRequestSurveyReportRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
    readonly reportType: ReportType.ReportType;
    readonly linkType: RequestEnums.LinkType.LinkType;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID, 
    reportType: ReportType.ReportType, 
    linkType: RequestEnums.LinkType.LinkType
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
      this.reportType = reportType;
      this.linkType = linkType;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const reportType = ReportType.fromDTO(getObject(dto, "reportType", InvalidRequestReportTypeError));
      const linkType = RequestEnums.LinkType.fromDTO(getObject(dto, "linkType", InvalidRequestLinkTypeError));
      return new Request(
        surveyId,
        projectId,
        reportType,
        linkType
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO(),
          reportType: ReportType.toDTO(this.reportType),
          linkType: RequestEnums.LinkType.toDTO(this.linkType)
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `RequestSurveyReportRPC(surveyId='${this.surveyId}',projectId='${this.projectId}',reportType='${this.reportType}',linkType='${this.linkType}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `RequestSurveyReportRPC.Response()`;
  };

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    SurveyIsInDraftState = "SURVEY_IS_IN_DRAFT_STATE",
    NoSurveyAnswerOrLinkFound = "NO_SURVEY_ANSWER_OR_LINK_FOUND"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `RequestSurveyReportRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `RequestSurveyReportRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface SurveyIsInDraftStateDTO {
      code: string;
    }
    
    export class SurveyIsInDraftState extends LeoRPCError<RPCError> {
      readonly isSurveyIsInDraftState: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsInDraftState;
        super(code);
      }
    
    
      toDTO(): SurveyIsInDraftStateDTO {
        const dto: SurveyIsInDraftStateDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsInDraftState.prototype.toString = function toString(): string {
      return `RequestSurveyReportRPC.SURVEY_IS_IN_DRAFT_STATE()`;
    };
    
    
    export interface NoSurveyAnswerOrLinkFoundDTO {
      code: string;
    }
    
    export class NoSurveyAnswerOrLinkFound extends LeoRPCError<RPCError> {
      readonly isNoSurveyAnswerOrLinkFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.NoSurveyAnswerOrLinkFound;
        super(code);
      }
    
    
      toDTO(): NoSurveyAnswerOrLinkFoundDTO {
        const dto: NoSurveyAnswerOrLinkFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    NoSurveyAnswerOrLinkFound.prototype.toString = function toString(): string {
      return `RequestSurveyReportRPC.NO_SURVEY_ANSWER_OR_LINK_FOUND()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | SurveyIsInDraftState | NoSurveyAnswerOrLinkFound;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | SurveyIsInDraftStateDTO | NoSurveyAnswerOrLinkFoundDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.SurveyIsInDraftState:
          return new SurveyIsInDraftState();
        case RPCError.NoSurveyAnswerOrLinkFound:
          return new NoSurveyAnswerOrLinkFound();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends RequestSurveyReportRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends RequestSurveyReportRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestReportTypeError extends RequestSurveyReportRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestReportTypeError";
  }
}

export class InvalidRequestLinkTypeError extends RequestSurveyReportRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLinkTypeError";
  }
}

export class InvalidRequestLinkTypeLiveError extends InvalidRequestLinkTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLinkTypeLiveError";
  }
}

export class InvalidRequestLinkTypeTestError extends InvalidRequestLinkTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLinkTypeTestError";
  }
}


export class InvalidResponseInvalidSurveyIdError extends RequestSurveyReportRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends RequestSurveyReportRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseSurveyIsInDraftStateError extends RequestSurveyReportRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsInDraftStateError";
  }
}

export class InvalidResponseNoSurveyAnswerOrLinkFoundError extends RequestSurveyReportRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNoSurveyAnswerOrLinkFoundError";
  }
}

